import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import styles from './AdminSuggestions.module.css';
import { Lightbulb, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/toastUtils.jsx';

const AdminSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSuggestions = async () => {
    try {
      const { data } = await api.get('/admin/suggestions');
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to fetch suggestions', error);
      toast.error('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/admin/suggestions/${id}/status`, { status });
      setSuggestions(suggestions.map(s => s._id === id ? { ...s, status } : s));
      toast.success(`Suggestion marked as ${status}`);
    } catch (error) {
      console.error('Failed to update status', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    confirmAction('Are you sure you want to delete this suggestion?', async () => {
      try {
        await api.delete(`/admin/suggestions/${id}`);
        setSuggestions(suggestions.filter(s => s._id !== id));
        toast.success('Suggestion deleted');
      } catch (error) {
        console.error('Failed to delete suggestion', error);
        toast.error('Failed to delete suggestion');
      }
    });
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return <div className={styles.loading}>Loading suggestions...</div>;
  }

  return (
    <div className={styles.suggestionsContainer}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Lightbulb className={styles.headerIcon} size={28} />
          <h1>Resident Suggestions</h1>
        </div>
        <p className={styles.subtitle}>Manage suggestions submitted via the public amenities page.</p>
      </div>

      <div className={styles.suggestionsList}>
        {suggestions.length === 0 ? (
          <div className={styles.noData}>No suggestions found.</div>
        ) : (
          suggestions.map((suggestion) => (
            <div key={suggestion._id} className={`${styles.suggestionCard} ${styles[suggestion.status.toLowerCase()]}`}>
              <div className={styles.cardHeader}>
                <div className={styles.userInfo}>
                  <h3>{suggestion.name || 'Anonymous Resident'}</h3>
                  <span className={styles.date}>{formatDate(suggestion.createdAt)}</span>
                </div>
                <div className={styles.statusBadge}>{suggestion.status}</div>
              </div>
              
              <div className={styles.contactInfo}>
                {suggestion.email && <span>{suggestion.email}</span>}
                {suggestion.phone && <span>{suggestion.phone}</span>}
              </div>

              <div className={styles.suggestionContent}>
                <p>{suggestion.suggestion}</p>
              </div>

              <div className={styles.cardActions}>
                {suggestion.status === 'New' && (
                  <>
                    <button 
                      className={`${styles.actionBtn} ${styles.reviewBtn}`}
                      onClick={() => handleStatusChange(suggestion._id, 'Reviewed')}
                    >
                      <CheckCircle size={16} /> Mark Reviewed
                    </button>
                    <button 
                      className={`${styles.actionBtn} ${styles.dismissBtn}`}
                      onClick={() => handleStatusChange(suggestion._id, 'Dismissed')}
                    >
                      <XCircle size={16} /> Dismiss
                    </button>
                  </>
                )}
                <button 
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  onClick={() => handleDelete(suggestion._id)}
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminSuggestions;
